<script setup lang="ts">
import Badge from '../Common/Badge/Badge.vue'
import { computed } from 'vue'
import type { Product, ProductReview, ProductReviewVote } from '~/types/products'
import type { Profile } from '~/types/profile'
import Stars from '~/components/Product/Stars.vue'
import Avatar from '~/components/Avatar.vue'
import { useProductReviewsStore } from '~/stores/product/Reviews'
import { useRouter } from '#imports'
import { useProductsStore } from '~/stores/product/Products'
import type { ToastNotification } from '~/components/Toast.vue'
const { $navigate } = useNuxtApp()

interface Props {
	review: ProductReview
	isLoading: boolean
	product: Product
	showProduct?: boolean
	showTypeTag?: boolean
}

const props = defineProps<Props>()

const isAuthed = inject('isAuthed') as Ref<boolean>
const profile = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>

const localReview = ref<ProductReview>(props.review)

const router = useRouter()
const reviewStore = useProductReviewsStore(props.product.id)
const productStore = useProductsStore()

const upvoted = computed(
	() => !!localReview.value?.product_reviews_upvotes?.find((v: ProductReviewVote) => v.user_id === profile?.value?.id),
)
const downvoted = computed(
	() => !!localReview.value?.product_reviews_downvotes?.find((v: ProductReviewVote) => v.user_id === profile?.value?.id),
)

async function upvote() {
	if (!isAuthed.value) {
		const title = `Login To Upvote`
		const message = `Please login (or register for free) to upvote this product review. Once completed, you will be redirected back to this product review.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	}

	try {
		await reviewStore().upvote({
			product_review_id: props.review.id,
			user_id: profile.value.id,
		})
		localReview.value = await reviewStore().get(props.review.id) as ProductReview
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Review upvoted',
			message: 'You have successfully upvoted this review.',
			type: 'success',
		}

	} catch (error) {
		console.error('Error upvoting review:', error)
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Error upvoting review',
			message: 'There was an error upvoting this review. Please try again.',
			type: 'error',
		}
	}
}

async function removeUpvote() {
	if (!isAuthed.value) {
		const title = `Login To Remove Upvote`
		const message = `Please login (or register for free) to remove your upvote from this product review. Once completed, you will be redirected back to this product review.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	}

	try {
		await reviewStore().removeUpvote({
			product_review_id: props.review.id,
			user_id: profile.value.id,
		})
		localReview.value = await reviewStore().get(props.review.id) as ProductReview
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Upvote removed',
			message: 'You have successfully removed your upvote.',
			type: 'success',
		}
	} catch (error) {
		console.error('Error removing upvote:', error)
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Error removing upvote',
			message: 'There was an error removing your upvote. Please try again.',
			type: 'error',
		}
	}
}

async function downvote() {
	if (!isAuthed.value) {
		const title = `Login To Downvote`
		const message = `Please login (or register for free) to downvote this product review. Once completed, you will be redirected back to this product review.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	}

	try {
		await reviewStore().downvote({
			product_review_id: props.review.id,
			user_id: profile.value.id,
		})
		localReview.value = await reviewStore().get(props.review.id) as ProductReview
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Review downvoted',
			message: 'You have successfully downvoted this review.',
			type: 'success',
		}

	} catch (error) {
		console.error('Error downvoting review:', error)
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Error downvoting review',
			message: 'There was an error downvoting this review. Please try again.',
			type: 'error',
		}
	}
}

async function removeDownvote() {
	if (!isAuthed.value) {
		const title = `Login To Remove Downvote`
		const message = `Please login (or register for free) to remove your downvote from this product review. Once completed, you will be redirected back to this product review.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	}

	try {
		await reviewStore().removeDownvote({
			product_review_id: props.review.id,
			user_id: profile.value.id,
		})
		localReview.value = await reviewStore().get(props.review.id) as ProductReview
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Downvote removed',
			message: 'You have successfully removed your downvote.',
			type: 'success',
		}

	} catch (error) {
		console.error('Error removing downvote:', error)
		toast.value = {
			show: true,
			timeout: 10,
			title: 'Error removing downvote',
			message: 'There was an error removing your downvote. Please try again.',
			type: 'error',
		}
	}
}

function getProductURL() {
	return productStore().getURL(props.product)
}
</script>

<template>
	<article
		:id="`review-${review.id}`"
		:class="[review.users?.[0]?.id === profile?.id ? 'bg-green-50' : 'bg-white', 'card px-4 pt-3 sm:px-6']">
		<div class="flex flex-col gap-2">
			<header class="flex justify-between items-start border-b border-gray-100 pb-2 mb-2">
				<div class="flex items-center gap-2">
					<SkeletonLoader v-if="isLoading" class="w-8 h-8 rounded-sm" />
					<Avatar
						v-else
						:is-authed="isAuthed"
						:self="profile"
						:profile="review.users?.[0] as Profile"
						:size="10"
						type="rounded"
						text-size="text-sm" />
					<div class="flex flex-col gap-1">
						<SkeletonLoader v-if="isLoading" class="w-20 h-4" />
						<span v-else class="text-sm text-gray-500">{{ review.users?.[0]?.username }}</span>
						<SkeletonLoader v-if="isLoading" class="w-16 h-4" />
						<span v-else class="text-xs text-gray-400">{{ ago(review.created_at) }}</span>
					</div>
				</div>
				<SkeletonLoader v-if="isLoading" class="w-16 h-4" />
				<div v-else class="flex flex-col items-end">
					<RouterLink :to="getProductURL()" class="no-underline"
						><h3 class="text-sm font-semibold text-green-500 m-0 p-0">{{ product.name }}</h3></RouterLink
					>
					<Stars :rating="review.rating" size="md" data-test="review-rating" />
				</div>
			</header>

			<div>
				<SkeletonLoader v-if="isLoading" class="w-full h-24" />
				<span v-else v-html="review.content" class="text-gray-500 text-sm sm:text-base" />
			</div>

			<div class="flex items-center border-t border-gray-100">
				<div class="flex items-center gap-4 flex-1">
					<SkeletonLoader v-if="isLoading" class="w-4 h-4 rounded-sm" />
					<SkeletonLoader v-if="isLoading" class="w-4 h-4 rounded-sm" />

					<div
						v-if="!isLoading && review.users?.[0]?.id !== profile?.id"
						class="flex items-center gap-1 my-2">
						<!-- Upvote -->
						<NuxtLink
							v-if="!upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="upvote">
							<Icon
								name="ph:thumbs-up-duotone"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<NuxtLink
							v-else-if="upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="removeUpvote">
							<Icon name="ph:thumbs-up-fill" class="size-5 flex-none text-gray-500 hover:text-green-500" aria-hidden="true" />
						</NuxtLink>
						<div v-else class="flex items-center justify-center font-semibold text-gray-400">
							<Icon
								name="ph:thumbs-up-duotone"
								class="size-5 flex-none text-gray-400"
								aria-hidden="true" />
						</div>

						<!-- Downvote -->
						<NuxtLink
							v-if="!upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="downvote">
							<Icon
								name="ph:thumbs-down-duotone"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<NuxtLink
							v-else-if="!upvoted && downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="removeDownvote">
							<Icon
								name="ph:thumbs-down-fill"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<div v-else class="flex items-center justify-center font-semibold text-gray-400">
							<Icon
								name="ph:thumbs-down-duotone"
								class="size-5 flex-none text-gray-400"
								aria-hidden="true" />
						</div>
						<span class="text-sm">{{
							(localReview.product_reviews_upvotes?.length ?? 0) - (localReview.product_reviews_downvotes?.length ?? 0)
						}}</span>

					</div>

					<div v-if="!isLoading && review.users?.[0]?.id === profile?.id" class="flex items-center gap-1">
						<Icon name="ph:thumbs-up-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						<span class="text-xs">{{ review.votes }}</span>
					</div>
				</div>

				<div class="flex flex-row items-center gap-0 px-0 lg:px-2 pt-2 pb-2 ">
					<div v-if="review.order_id" class="hidden lg:block text-sm text-gray-500" data-test="verified-purchase">
						Verified Purchase - {{ new Date(review.order_date!).toLocaleDateString() }}
					</div>

					<Badge v-if="showTypeTag" color="purple" class="ml-2">REVIEW</Badge>

				</div>
			</div>
		</div>
	</article>
</template>
